import { Ref, ref } from 'vue'

const backendUrl = process.env.VUE_APP_API_BASE_URL

export default class HttpService {
  token: Ref<string | undefined> = ref(undefined)
  private redirectingToLogin = false

  private constructor () {
    // Check if an instance already exists and return it
    if (instance) {
      return instance
    }
    // If no instance exists, create one and assign it to the 'instance' variable
    instance = this
  }

  static async getInstance () {
    const httpService = new HttpService()
    return httpService
  }

  setToken (token: string) {
    localStorage.setItem('idToken', token)
  }

  getToken () {
    return localStorage.getItem('idToken')
  }

  removeToken () {
    localStorage.removeItem('idToken')
  }

  private async redirectToLoginIfUnauthorized (response: Response): Promise<void> {
    if (response.status !== 401) return

    this.removeToken()

    if (!this.redirectingToLogin) {
      this.redirectingToLogin = true
      window.location.replace(process.env.BASE_URL || '/')
    }

    // Keep callers from showing operation-specific errors while the app reloads.
    await new Promise<never>(() => {})
  }

  async get (path: string, queryParams: any = {}, responseType: string = 'json') {
    const headers = await this.getHeaders()
    const queryString = Object.entries(queryParams).map(([key, value]) => `${key}=${value}`).join('&')
    const url = `${backendUrl}${path}${queryString ? `?${queryString}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'include'
    })

    await this.redirectToLoginIfUnauthorized(response)

    if (response.ok) {
      if (responseType === 'json') {
        return await response.json()
      } else if (responseType === 'text') {
        return await response.text()
      } else if (responseType === 'blob') {
        return await response.blob()
      } else {
        throw new Error(`Unsupported response type: ${responseType}`)
      }
    }

    const errorDetail = await this.extractErrorDetail(response)
    throw new Error(errorDetail)
  }

  async getHeaders () {
    const token = this.getToken()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  }

  private async extractErrorDetail (response: Response): Promise<string> {
    const method = response.status ? `${response.status}` : 'unknown'
    try {
      const body = await response.json()
      const message = body?.detail ?? body?.message ?? body?.error?.message
      if (message) return `${method}: ${message}`
    } catch {
      // body is not JSON
    }
    return `${method}: ${response.statusText || 'Request failed'}`
  }

  async post (path: string, body: any) {
    const headers = await this.getHeaders()

    const response = await fetch(backendUrl + path, {
      method: 'POST',
      headers,
      mode: 'cors',
      credentials: 'include',
      body: JSON.stringify(body)
    })

    await this.redirectToLoginIfUnauthorized(response)

    if (response.ok) {
      return await response.json()
    }

    const errorDetail = await this.extractErrorDetail(response)
    throw new Error(errorDetail)
  }

  async delete (path: string, body: any = {}) {
    const headers = await this.getHeaders()

    const response = await fetch(backendUrl + path, {
      method: 'DELETE',
      headers,
      mode: 'cors',
      credentials: 'include',
      body: JSON.stringify(body)
    })

    await this.redirectToLoginIfUnauthorized(response)

    if (response.ok || response.status === 204) {
      return
    }

    const errorDetail = await this.extractErrorDetail(response)
    throw new Error(errorDetail)
  }

  async patch (path: string, body: any) {
    const headers = await this.getHeaders()

    const response = await fetch(backendUrl + path, {
      method: 'PATCH',
      headers,
      mode: 'cors',
      credentials: 'include',
      body: JSON.stringify(body)
    })

    await this.redirectToLoginIfUnauthorized(response)

    if (response.ok || response.status === 204) {
      return await response.json()
    }

    const errorDetail = await this.extractErrorDetail(response)
    throw new Error(errorDetail)
  }
}

let instance: HttpService | null = null // Singleton instance
